import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { changePassword } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Forced on first login (or after an admin password reset): the member must
// choose a new password before accessing the app.
const ChangePassword = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pending) return;
    if (next !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    if (next.length < 12) {
      setError("Le nouveau mot de passe doit contenir au moins 12 caractères.");
      return;
    }
    setError("");
    setPending(true);
    try {
      await changePassword({ current_password: current, new_password: next });
      setUser({ ...user, must_change_password: false });
      toast.success("Mot de passe mis à jour");
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Impossible de changer le mot de passe.");
    } finally {
      setPending(false);
    }
  };

  return (
    <div data-testid="change-password-page" className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <span className="h-14 w-14 rounded-2xl bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-600/25">
            <KeyRound className="h-7 w-7 text-white" strokeWidth={2.2} />
          </span>
          <h1 className="font-heading font-bold text-slate-900 text-2xl tracking-tight mt-4">Nouveau mot de passe</h1>
          <p className="text-sm text-slate-500 mt-1 text-center">
            Première connexion : choisissez un mot de passe personnel pour continuer.
          </p>
        </div>

        <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 space-y-4">
          {error && (
            <div data-testid="change-password-error" role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div>
            <Label htmlFor="cp-current" className="text-sm">Mot de passe temporaire</Label>
            <Input id="cp-current" data-testid="change-password-current-input" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1 h-11 text-base" />
          </div>
          <div>
            <Label htmlFor="cp-new" className="text-sm">Nouveau mot de passe</Label>
            <Input id="cp-new" data-testid="change-password-new-input" type="password" autoComplete="new-password" required value={next} onChange={(e) => setNext(e.target.value)} className="mt-1 h-11 text-base" placeholder="12 caractères minimum" />
          </div>
          <div>
            <Label htmlFor="cp-confirm" className="text-sm">Confirmer le nouveau mot de passe</Label>
            <Input id="cp-confirm" data-testid="change-password-confirm-input" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 h-11 text-base" />
          </div>
          <button
            type="submit"
            data-testid="change-password-submit"
            disabled={pending || !current || !next || !confirm}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold py-3 hover:bg-emerald-700 active:scale-[0.99] disabled:opacity-50 transition-all"
          >
            {pending ? "Enregistrement…" : "Valider mon mot de passe"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
